import { getUserIdWithFallback } from '../../../utils/getUserIdFromToken';
import { evaluateResponseErrorCode, getApiError } from '../../errors';
import { post } from '../../http';

export interface ActivateAuthenticatorResponse {
  success: boolean;
  message?: string;
}

/**
 * Activates an authenticator for a guest using the correct user ID from JWT token
 * @param authenticatorId - The ID of the authenticator to activate
 * @param requestBody - Request payload with activation details (optional)
 * @returns Promise with activation response
 *
 * The user ID (mgmid) is automatically extracted from the JWT token's com.mgm.id claim.
 * This fixes the "mgmid in the token does not match the request" error.
 *
 * Note: Authorization header is automatically added by the http module from window.mgm.authClient
 */
export async function activateAuthenticator(
  authenticatorId: string,
  requestBody?: Record<string, unknown>,
): Promise<ActivateAuthenticatorResponse> {
  // Extract user ID from JWT token's com.mgm.id claim
  const mgmid = await getUserIdWithFallback();

  if (!mgmid) {
    throw new Error('Unable to extract user ID from token. Please ensure you are properly authenticated.');
  }

  try {
    console.log('🔐 Activating authenticator for user ID:', mgmid, 'authenticatorId:', authenticatorId);

    const result = await post(`/guest/${mgmid}/authenticators/${authenticatorId}/activate`, {
      body: JSON.stringify(requestBody || {}),
    });
    evaluateResponseErrorCode(result.response);

    return result.response;
  } catch (error) {
    throw getApiError(error);
  }
}

/**
 * @deprecated Use activateAuthenticator() without mgmid parameter instead.
 * This function is kept for backward compatibility but will be removed in future versions.
 */
export async function activateAuthenticatorLegacy(
  mgmid: string,
  authenticatorId: string,
  requestBody?: Record<string, unknown>,
): Promise<ActivateAuthenticatorResponse> {
  console.warn('activateAuthenticatorLegacy is deprecated. Use activateAuthenticator() instead.');

  try {
    const result = await post(`/guest/${mgmid}/authenticators/${authenticatorId}/activate`, {
      body: JSON.stringify(requestBody || {}),
    });
    evaluateResponseErrorCode(result.response);

    return result.response;
  } catch (error) {
    throw getApiError(error);
  }
}
